<template>
  <div class="alerts-container">
    <!-- Expiring Soon -->
    <div class="alert-section">
      <div class="alert-section-header" style="background-color: #ffc107; color: #333;">
        <span class="icon">
          <i class="fas fa-clock"></i>
        </span>
        <span class="has-text-weight-semibold is-size-7">
          Expiring Soon 
          <span v-if="alertData.expiring_soon && alertData.expiring_soon.length > 0">({{ alertData.expiring_soon.length }})</span>
        </span>
      </div>
      <div class="alert-section-content">
        <div v-if="alertData.expiring_soon && alertData.expiring_soon.length > 0">
          <div v-for="invoice in alertData.expiring_soon" :key="invoice.invoice_number" class="alert-item">
            <div class="is-flex is-justify-content-space-between is-align-items-center">
              <div class="flex-grow-1">
                <div class="has-text-weight-semibold is-size-7">{{ invoice.invoice_number }}</div>
                <div class="is-size-7 has-text-grey">{{ formatDate(invoice.invoice_date) }}</div>
              </div>
              <div>
                <span class="tag is-size-7" style="background-color: #ffc107; color: #333;">{{ invoice.days_since_invoice }} days</span>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="alert-item has-text-centered has-text-grey">
          <span class="is-size-7">No invoices expiring soon</span>
        </div>
      </div>
    </div>

    <!-- Resubmit Reminders -->
    <div class="alert-section">
      <div class="alert-section-header" style="background-color: #dc3545;">
        <span class="icon">
          <i class="fas fa-exclamation-triangle"></i>
        </span>
        <span class="has-text-weight-semibold is-size-7">
          Resubmit Required 
          <span v-if="alertData.resubmit_reminders && alertData.resubmit_reminders.length > 0">({{ alertData.resubmit_reminders.length }})</span>
        </span>
      </div>
      <div class="alert-section-content">
        <div v-if="alertData.resubmit_reminders && alertData.resubmit_reminders.length > 0">
          <div v-for="invoice in alertData.resubmit_reminders" :key="invoice.invoice_number" class="alert-item">
            <div class="is-flex is-justify-content-space-between is-align-items-center">
              <div class="flex-grow-1">
                <div class="has-text-weight-semibold is-size-7">{{ invoice.invoice_number }}</div>
                <div class="is-size-7 has-text-grey">Invoice: {{ formatDate(invoice.invoice_date) }}</div>
                <div class="is-size-7" style="color: #dc3545;">Failed: {{ formatDateTime(invoice.failed_since) }}</div>
              </div>
              <div>
                <button 
                  class="button is-small is-outlined"
                  style="border-color: #dc3545; color: #dc3545;"
                  @click="handleResubmit(invoice.invoice_id)"
                  disabled
                >
                  <span class="icon is-small">
                    <i class="fas fa-redo"></i>
                  </span>
                  <span class="is-size-7">Resubmit</span>
                </button>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="alert-item has-text-centered has-text-grey">
          <span class="is-size-7">No invoices require resubmission</span>
        </div>
      </div>
    </div>

    <!-- Pending Approval -->
    <div class="alert-section">
      <div class="alert-section-header" style="background-color: #dc3545;">
        <span class="icon">
          <i class="fas fa-pause-circle"></i>
        </span>
        <span class="has-text-weight-semibold is-size-7">
          Pending Approval 
          <span v-if="alertData.cancelled_pending_approval && alertData.cancelled_pending_approval.length > 0">({{ alertData.cancelled_pending_approval.length }})</span>
        </span>
      </div>
      <div class="alert-section-content">
        <div v-if="alertData.cancelled_pending_approval && alertData.cancelled_pending_approval.length > 0">
          <div v-for="invoice in alertData.cancelled_pending_approval" :key="invoice.invoice_number" class="alert-item">
            <div class="is-flex is-justify-content-space-between is-align-items-center">
              <div class="flex-grow-1">
                <div class="has-text-weight-semibold is-size-7">{{ invoice.invoice_number }}</div>
                <div class="is-size-7 has-text-grey">Invoice: {{ formatDate(invoice.invoice_date) }}</div>
                <div class="is-size-7" style="color: #dc3545;">Pending: {{ formatDateTime(invoice.pending_since) }}</div>
              </div>
              <div>
                <span class="tag is-size-7" style="background-color: #dc3545; color: white;">Approval</span>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="alert-item has-text-centered has-text-grey">
          <span class="is-size-7">No invoices pending approval</span>
        </div>
      </div>
    </div>

    <!-- Tax Compliance Alert -->
    <div class="notification is-light mt-4" style="background-color: #fff3cd; border-left: 4px solid #ffc107;" v-if="showTaxAlert && hasAnyAlerts">
      <button class="delete is-small" @click="dismissTaxAlert"></button>
      <div class="is-size-7">
        <strong>Tax Compliance Alert:</strong> Please ensure all invoices are submitted within the required timeframe to maintain compliance.
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'UpcomingAlerts',
  props: {
    data: {
      type: Object,
      default: () => ({
        expiring_soon: [],
        resubmit_reminders: [],
        cancelled_pending_approval: []
      })
    }
  },
  data() {
    return {
      showTaxAlert: true
    }
  },
  computed: {
    alertData() {
      return this.data;
    },
    hasAnyAlerts() {
      return (this.alertData.expiring_soon && this.alertData.expiring_soon.length > 0) ||
             (this.alertData.resubmit_reminders && this.alertData.resubmit_reminders.length > 0) ||
             (this.alertData.cancelled_pending_approval && this.alertData.cancelled_pending_approval.length > 0);
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: '2-digit'
        });
      } catch (error) {
        return dateString;
      }
    },
    formatDateTime(dateTimeString) {
      if (!dateTimeString) return 'N/A';
      try {
        const date = new Date(dateTimeString);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        return dateTimeString;
      }
    },
    handleResubmit(invoiceId) {
      this.$emit('resubmit', invoiceId);
    },
    dismissTaxAlert() {
      this.showTaxAlert = false;
    }
  }
}
</script>

<style scoped>
.alerts-container {
  background: transparent;
  border-radius: 0;
  padding: 0;
  box-shadow: none;
}

.alert-section {
  margin-bottom: 1.5rem;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.alert-section-header {
  padding: 0.75rem 1rem;
  color: white;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.alert-section-content {
  background: rgba(255, 255, 255, 0.95);
  padding: 0;
}

.alert-item {
  padding: 1rem;
  border-bottom: 1px solid rgba(10, 57, 84, 0.1);
}

.alert-item:last-child {
  border-bottom: none;
}

.card-header {
  border-radius: 8px 8px 0 0;
}

.card {
  height: 100%;
}
</style>